import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingList } from '../types';
import ListCard from '../components/ListCard';
import EditListModal from '../components/EditListModal';
import DeleteListModal from '../components/DeleteListModal';
import { Plus } from 'lucide-react';
import { api } from '../utils/api';

const MyLists: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lists, setLists] = useState<ShoppingList[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [editingList, setEditingList] = useState<ShoppingList | null>(null);
  const [deletingList, setDeletingList] = useState<ShoppingList | null>(null);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const fetchLists = useCallback(async () => {
    console.log('fetchLists called with user:', user);
    if (!user?.id) {
      console.log('No user ID, setting loading to false');
      setLoading(false);
      return;
    }
    
    try {
      console.log('Fetching lists for user ID:', user.id);
      setLoading(true);
      const response = await api.get('/lists');
      console.log('Lists response:', response.status, response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Lists data:', data);
        setLists(data || []);
      } else {
        console.error('Failed to fetch lists:', response.status);
        setLists([]);
      }
    } catch (error) {
      console.error('Error fetching lists:', error);
      setLists([]);
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    console.log('MyLists useEffect triggered with user:', user);
    if (user) {
      console.log('User exists, calling fetchLists');
      fetchLists();
    } else {
      console.log('No user, setting loading to false');
      setLoading(false);
    }
  }, [user, fetchLists]);

  // Add timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn('Loading timeout - setting loading to false');
        setLoading(false);
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
  }, [loading]);

  const createList = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newListName.trim() || !user) return;

    try {
      const response = await api.post('/lists', {
        name: newListName.trim(),
      });

      if (response.ok) {
        const newList = await response.json();
        setLists([...lists, newList]);
        setNewListName('');
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error('Error creating list:', error);
    }
  };

  const handleListClick = (listId: string) => {
    navigate(`/list/${listId}`);
  };

  const handleEditList = (list: ShoppingList) => {
    setEditingList(list);
  };

  const handleDeleteList = (list: ShoppingList) => {
    setDeletingList(list);
  };

  const handleRemoveList = async (list: ShoppingList) => {
    if (!user) return;
    try {
      const response = await api.delete(`/lists/${list.id}/leave`);
      if (response.ok) {
        setLists(lists.filter(l => l.id !== list.id));
      } else {
        const data = await response.json().catch(() => ({}));
        console.error('Failed to remove list:', data.error || response.status);
      }
    } catch (error) {
      console.error('Error removing list:', error);
    }
  };

  const handleSaveEdit = async (newName: string) => {
    if (!editingList || !user) return;

    setIsEditLoading(true);
    try {
      const response = await api.patch(`/lists/${editingList.id}`, {
        name: newName,
      });

      if (response.ok) {
        const updatedList = await response.json();
        setLists(lists.map(list => 
          list.id === editingList.id ? updatedList : list
        ));
        setEditingList(null);
      } else {
        console.error('Failed to update list:', response.status);
        // You could add error handling here
      }
    } catch (error) {
      console.error('Error updating list:', error);
      // You could add error handling here
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingList || !user) return;

    setIsDeleteLoading(true);
    try {
      const response = await api.delete(`/lists/${deletingList.id}`);

      if (response.ok) {
        setLists(lists.filter(list => list.id !== deletingList.id));
        setDeletingList(null);
      } else {
        console.error('Failed to delete list:', response.status);
        // You could add error handling here
      }
    } catch (error) {
      console.error('Error deleting list:', error);
      // You could add error handling here
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const isListOwner = (list: ShoppingList) => {
    return user?.id === list.created_by;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your lists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Create List Section */}
        <div className="mb-8">
          {showCreateForm ? (
            <form onSubmit={createList} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New List</h2>
              <div className="flex space-x-4">
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="Enter list name..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewListName('');
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors shadow-sm"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create New List
            </button>
          )}
        </div>

        {/* Lists Grid */}
        {lists.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No lists yet</h3>
            <p className="text-gray-600 mb-6">Create your first shopping list to get started</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create List
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lists.map((list) => (
              <ListCard
                key={list.id}
                list={list}
                onClick={() => handleListClick(list.id)}
                onEdit={handleEditList}
                onDelete={handleDeleteList}
                onRemoveList={handleRemoveList}
                isOwner={isListOwner(list)}
              />
            ))}
          </div>
        )}

      {/* Edit List Modal */}
      {editingList && (
        <EditListModal
          isOpen={!!editingList}
          onClose={() => setEditingList(null)}
          onSave={handleSaveEdit}
          currentName={editingList.name}
          isLoading={isEditLoading}
        />
      )}

      {/* Delete List Modal */}
      {deletingList && (
        <DeleteListModal
          isOpen={!!deletingList}
          onClose={() => setDeletingList(null)}
          onConfirm={handleConfirmDelete}
          listName={deletingList.name}
          isLoading={isDeleteLoading}
        />
      )}
    </div>
  );
};

export default MyLists;
